import {createContext, type ReactNode, use, useCallback, useMemo, useRef} from 'react'

// Arbitrary input elements that can affect navigating away from the current issue
type InputElementStates = Readonly<Record<string, boolean>>

type InputElementActiveContextType = {
  isAnyInputElementActive: () => boolean
  setInputElementState: (id: string, state: boolean) => void
  clearInputElementStates: () => void
}

const InputElementActiveContext = createContext<InputElementActiveContextType | null>(null)

type InputElementActiveContextProviderType = {
  children: ReactNode
}

const defaultEmptyState = {}
export function InputElementActiveContextProvider({children}: InputElementActiveContextProviderType) {
  const inputElementStates = useRef<InputElementStates>(defaultEmptyState)
  const setInputElementState = useCallback((id: string, state: boolean) => {
    const previousInputElementStates = inputElementStates.current
    if (previousInputElementStates[id] === state) return inputElementStates.current
    inputElementStates.current = {
      ...previousInputElementStates,
      [id]: state,
    }
    return inputElementStates.current
  }, [])

  const isAnyInputElementActive = useCallback(() => {
    return Object.values(inputElementStates.current).some(s => s)
  }, [])

  const clearInputElementStates = useCallback(() => {
    inputElementStates.current = defaultEmptyState
  }, [])

  const contextValue: InputElementActiveContextType = useMemo(() => {
    return {
      setInputElementState,
      isAnyInputElementActive,
      clearInputElementStates,
    }
  }, [isAnyInputElementActive, setInputElementState, clearInputElementStates])

  return <InputElementActiveContext value={contextValue}>{children}</InputElementActiveContext>
}

export const useInputElementActiveContext = () => {
  const context = use(InputElementActiveContext)
  if (!context) {
    throw new Error('useInputElementActiveContext must be used within a InputElementActiveContextProvider.')
  }

  return context
}
